const STORAGE_KEY = 'lf-auth-activation-guard-until';
const OWNER_KEY = 'lf-auth-activation-owner';

// Long enough to cover a provider popup/redirect, but always bounded so a
// crashed activation tab cannot suppress normal no-access handling forever.
const ACTIVATION_TTL_MS = 5 * 60 * 1000;
const SETTLE_TTL_MS = 15 * 1000;
const RENEW_INTERVAL_MS = 30 * 1000;

interface ActivationGuard {
  owner: string;
  until: number;
}

function currentOwner(): string | null {
  try {
    return window.sessionStorage.getItem(OWNER_KEY);
  } catch {
    return null;
  }
}

function storeGuard(guard: ActivationGuard): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(guard));
  } catch {
    // Storage can be unavailable in hardened/private browser contexts. The
    // backend remains authoritative; this guard only prevents cross-tab UX races.
  }
}

export function beginAuthActivationGuard(now = Date.now()): () => void {
  try {
    const owner = window.crypto.randomUUID();
    window.sessionStorage.setItem(OWNER_KEY, owner);
    storeGuard({ owner, until: now + ACTIVATION_TTL_MS });
    const interval = window.setInterval(() => {
      if (currentOwner() === owner) {
        storeGuard({ owner, until: Date.now() + ACTIVATION_TTL_MS });
      }
    }, RENEW_INTERVAL_MS);
    return () => window.clearInterval(interval);
  } catch {
    // See storeGuard: the backend authorization path is unchanged.
    return () => {};
  }
}

export function settleAuthActivationGuard(now = Date.now()): void {
  // Keep a short grace period for protected requests that another tab started
  // before the invitation transaction committed but that finish afterwards.
  const owner = currentOwner();
  if (owner) storeGuard({ owner, until: now + SETTLE_TTL_MS });
}

export function clearAuthActivationGuard(): void {
  try {
    const owner = currentOwner();
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const stored = raw ? (JSON.parse(raw) as Partial<ActivationGuard>) : null;
    if (!stored || stored.owner === owner) window.localStorage.removeItem(STORAGE_KEY);
    window.sessionStorage.removeItem(OWNER_KEY);
  } catch {
    // See storeUntil: authorization does not depend on browser storage.
  }
}

export function isAuthActivationGuardActive(now = Date.now()): boolean {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const stored = raw ? (JSON.parse(raw) as Partial<ActivationGuard>) : null;
    const valid =
      typeof stored?.owner === 'string' &&
      stored.owner.length > 0 &&
      stored.owner.length <= 128 &&
      typeof stored.until === 'number' &&
      Number.isFinite(stored.until) &&
      stored.until > now &&
      stored.until <= now + ACTIVATION_TTL_MS;
    if (!valid) {
      window.localStorage.removeItem(STORAGE_KEY);
      return false;
    }
    return true;
  } catch {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Storage itself is unavailable; there is no persisted state we can trust.
    }
    return false;
  }
}
